// v1.0.0 - 2026-01-27 - Header component

import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils/cn'
import { Bell, ChevronDown, LogOut, User, Settings } from 'lucide-react'
import type { User as UserType } from '@/types'

interface HeaderProps {
  user: UserType | null
  onLogout: () => void
  title?: string
}

export function Header({ user, onLogout, title }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <header className="flex h-16 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-bg-primary)] px-6">
      {/* Left: Title */}
      <div className="flex items-center gap-4">
        {title && (
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">{title}</h1>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-4">
        {/* Notifications */}
        <button
          className="relative rounded-lg p-2 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
          aria-label="Notifiche"
        >
          <Bell className="h-5 w-5" />
          {/* Notification badge (optional) */}
          {/* <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[var(--color-error)]" /> */}
        </button>

        {/* User Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={cn(
              'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              'text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)]',
              isMenuOpen && 'bg-[var(--color-bg-secondary)]'
            )}
          >
            {/* Avatar */}
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-accent)] text-white text-xs font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <span className="hidden sm:block">{user?.name || 'Utente'}</span>
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', isMenuOpen && 'rotate-180')}
            />
          </button>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] py-1 shadow-lg animate-fade-in">
              {/* User Info */}
              <div className="border-b border-[var(--color-border)] px-4 py-3">
                <p className="text-sm font-medium text-[var(--color-text-primary)]">
                  {user?.name}
                </p>
                <p className="text-xs text-[var(--color-text-tertiary)]">{user?.email}</p>
                <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                  {user?.role === 'admin' ? 'Amministratore' : 'Membro'}
                </p>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <Link
                  to={user?.role === 'admin' ? '/admin/profile' : '/member/profile'}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
                >
                  <User className="h-4 w-4" />
                  Profilo
                </Link>

                {user?.role === 'admin' && (
                  <Link
                    to="/admin/settings"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
                  >
                    <Settings className="h-4 w-4" />
                    Impostazioni
                  </Link>
                )}
              </div>

              {/* Logout */}
              <div className="border-t border-[var(--color-border)] py-1">
                <button
                  onClick={() => {
                    setIsMenuOpen(false)
                    onLogout()
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[var(--color-error)] hover:bg-[var(--color-bg-error)]"
                >
                  <LogOut className="h-4 w-4" />
                  Esci
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header
